import clsx from 'clsx';

import type { ReactNode } from 'react';

export interface ProjectNameProps {
	/**
	 * The rendered project name — typically a link, heading, or plain text node.
	 * It keeps whatever styling and semantics the call site needs; this component
	 * only lays it out alongside the badge.
	 */
	children: ReactNode;
	/** Whether the project is a demo. The badge is shown only when true. */
	isDemo: boolean;
	/** Extra classes for the inline-flex wrapper. */
	className?: string;
	/** Extra classes for the demo badge. */
	badgeClassName?: string;
	/**
	 * Tailwind gap utility controlling the space between the name and the badge.
	 * Passed separately from `className` because the wrapper's default gap and an
	 * overriding gap utility both target `gap`, and the codebase has no
	 * tailwind-merge to resolve that conflict by class order.
	 */
	gapClassName?: string;
}

/**
 * Lays out a project name with a "Demo" badge beside it when the project is a
 * demo, giving every project-name display the same badged appearance. The name
 * child truncates within the available width while the badge stays full-size.
 */
export function ProjectName({ children, isDemo, className, badgeClassName, gapClassName = 'gap-2' }: ProjectNameProps) {
	return (
		<span className={clsx('inline-flex min-w-0 items-center', gapClassName, className)}>
			{children}
			{isDemo && (
				<span className='flex h-0 shrink-0 items-center'>
					<span
						className={clsx(
							'inline-flex items-center rounded bg-iris-a4 px-1 py-0.25 text-[10px] font-bold tracking-wider text-iris-a11 uppercase',
							badgeClassName,
						)}
					>
						Demo
					</span>
				</span>
			)}
		</span>
	);
}
